type TimeframeDatapoint = {
  start: Date;
  center?: Date;
  end?: Date;
};

/**
 * Gets if a datapoint is connected to a gap
 *
 * @param datapoint - given datapoint
 * @param nextDatapoint - following datapoint
 * @param threshold - given GapPolicy threshold
 * @returns
 */
export function getDatapointIsGap(
  datapoint: TimeframeDatapoint,
  nextDatapoint: TimeframeDatapoint,
  threshold: number = 0,
): boolean {
  return hasGap(datapoint, nextDatapoint, threshold);
}

/**
 * Detects a gap between current and next data point.
 *
 * The distance between data points is measured, then we compare it to the given threshold.
 * When the distance is bigger than the threshold, we consider it a gap.
 *
 * It treats the timestamps as timeframes setting the end date as the start date.
 *
 * @param currentDatapoint - given current TimeframeDatapoint
 * @param nextDatapoint - given next TimeframeDatapoint
 * @param threshold - threshold to detect gaps
 * @returns
 */
function hasGap(
  currentDatapoint: TimeframeDatapoint,
  nextDatapoint: TimeframeDatapoint | undefined,
  threshold: number,
): boolean {
  if (!currentDatapoint || !nextDatapoint) {
    return false;
  }

  const distance = getDistance(nextDatapoint, currentDatapoint);

  const hasGap = distance > 0;
  const isGapOutsideThreshold = threshold < distance;

  return hasGap && isGapOutsideThreshold;
}

/**
 * Gets the distance between two data points.
 *
 * @param nextDatapoint - given first data point
 * @param currentDatapoint - given second data point
 * @returns
 */
function getDistance(
  nextDatapoint: TimeframeDatapoint,
  currentDatapoint: TimeframeDatapoint,
) {
  return (
    nextDatapoint.start.getTime() - resolveEndDate(currentDatapoint).getTime()
  );
}

/**
 * Resolves the end Date of a given data point. When the end is undefined, it fallbacks to start.
 *
 * @param datapoint - given TimeseriesDatapoint
 * @returns
 */
function resolveEndDate(datapoint: TimeframeDatapoint): Date {
  return datapoint.end ?? datapoint.start;
}
